import { FormInput } from "@general-backoffice/core";
import React, { useCallback, useMemo } from "react";

import iaeData from "../../../data/iaeCodes.json";

/**
 * Get the section object for a given section ID
 */
interface IAECodeInfo {
  code: string;
  description: string;
  descriptionEn?: string;
  section: string;
  activityType?: string;
  irpfApplies?: boolean;
}

interface IAEOption {
  value: string;
  label: string;
  code: string;
  description: string;
  descriptionEn?: string;
  section: string;
  activityType?: string;
  irpfApplies?: boolean;
}

const getSectionById = (sectionId: number | string) => {
  return iaeData.sections.find((s) => s.id === sectionId);
};

/**
 * Get all IAE codes with their full info
 */
const getAllCodes = () => {
  return iaeData.codes.map((code) => {
    const section = getSectionById(code.section);
    return {
      ...code,
      sectionName: section?.name,
      sectionNameEn: section?.nameEn,
      irpfApplies: section?.irpfApplies,
      activityType: section?.activityType,
    };
  });
};

/**
 * Find an IAE code by its code value
 */
export const findIAECode = (codeValue: string | null) => {
  if (!codeValue) return null;
  const code = iaeData.codes.find((c) => c.code === codeValue);
  if (!code) return null;
  const section = getSectionById(code.section);
  return {
    ...code,
    sectionName: section?.name,
    sectionNameEn: section?.nameEn,
    irpfApplies: section?.irpfApplies,
    activityType: section?.activityType,
  };
};

/**
 * IAE Code Selector Component
 * Searchable dropdown for selecting IAE codes with auto-fill of description and activity type
 */
interface IAECodeSelectorProps {
  value: string | null | undefined;
  onChange: (code: string | null) => void;
  onCodeSelect?: (info: IAECodeInfo | null) => void;
  isDisabled: boolean;
  label: string;
  placeholder: string;
  language?: string;
}

const IAECodeSelector = ({
  value,
  onChange,
  onCodeSelect,
  isDisabled,
  label,
  placeholder,
  language = "es",
}: IAECodeSelectorProps) => {
  // Build grouped options for react-select
  const groupedOptions = useMemo(() => {
    const allCodes = getAllCodes();

    const groups = iaeData.sections.map((section) => {
      const sectionCodes = allCodes.filter((c) => c.section === section.id);
      return {
        label: language === "es" ? section.name : section.nameEn,
        options: sectionCodes.map((code) => ({
          value: code.code,
          label: `${code.code} - ${language === "es" ? code.description : code.descriptionEn}`,
          code: code.code,
          description: code.description,
          descriptionEn: code.descriptionEn,
          section: code.section,
          activityType: code.activityType,
          irpfApplies: code.irpfApplies,
        })),
      };
    });

    return groups.filter((g) => g.options.length > 0);
  }, [language]);

  // Find the selected option
  const selectedOption = useMemo(() => {
    if (!value) return null;
    for (const group of groupedOptions) {
      const option = group.options.find((o) => o.code === value);
      if (option) return option;
    }
    return null;
  }, [value, groupedOptions]);

  // Handle selection
  const handleChange = useCallback(
    (option: IAEOption | null) => {
      if (option) {
        // Notify parent of the code change
        onChange(option.code);

        // Also notify with full code info for auto-fill
        if (onCodeSelect) {
          onCodeSelect({
            code: option.code,
            description: option.description,
            descriptionEn: option.descriptionEn,
            section: option.section,
            activityType: option.activityType,
            irpfApplies: option.irpfApplies,
          });
        }
      } else {
        onChange(null);
        if (onCodeSelect) {
          onCodeSelect(null);
        }
      }
    },
    [onChange, onCodeSelect]
  );

  // Custom filter function for better search
  const filterOption = useCallback((option: { data: IAEOption }, inputValue: string) => {
    if (!inputValue) return true;
    const searchLower = inputValue.toLowerCase();
    const code = option.data.code.toLowerCase();
    const description = option.data.description.toLowerCase();
    const descriptionEn = option.data.descriptionEn?.toLowerCase() || "";

    return code.includes(searchLower) || description.includes(searchLower) || descriptionEn.includes(searchLower);
  }, []);

  return (
    <FormInput.Select
      label={label}
      name="iaeCode"
      value={selectedOption}
      options={groupedOptions}
      onChange={handleChange}
      isDisabled={isDisabled}
      placeholder={placeholder}
      isClearable
      isSearchable
      filterOption={filterOption}
      noOptionsMessage={() => (language === "es" ? "No se encontraron códigos" : "No codes found")}
    />
  );
};

export default IAECodeSelector;
